import { Link, useLocation } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BadgeCheck, ExternalLink, DollarSign, Calendar, Briefcase, Sparkles } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Button } from "@/components/ui/button";
import { FreelancerListItem } from "@/types/freelancer";
import { formatTimeAgo } from "@/utils/dateUtils";
import { FreelancerActionsMenu } from "../FreelancerActionsMenu";
import { KanbanStageSelector } from "../KanbanStageSelector";
import { FreelancerScoreBadge } from "./FreelancerScoreBadge";
import { FreelancerSignalsRow } from "./FreelancerSignalsRow";
import { FreelancerAreasSummary } from "./FreelancerAreasSummary";
import { SearchHighlight } from "./SearchHighlight";
import { FreelancerSearchReasons } from "./FreelancerSearchReasons";
import { reasonsHiddenFromCard, type FreelancerSearchMatch } from "./searchMatch";
import { buildAvailabilityChip, buildFichaUrl, formatLocation, topAttributes } from "./freelancerCardShared";
import { FreelancerAvatar } from "./FreelancerAvatar";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { cn } from "@/lib/utils";
import type { FreelancerResumoRapido } from "@/types/freelancer-painel";
import { LinkedInIcone, WhatsAppIcone } from "@/components/freelancers/contato/FreelancerContatoIcons";

import {
  DisponibilidadeBadge,
  DisponibilidadeDataHint,
} from "@/components/freelancers/DisponibilidadeBadge";
import { AlocacaoChip } from "@/components/freelancers/AlocacaoChip";
import type { DisponibilidadeSummary } from "@/utils/disponibilidadeUtils";

interface FreelancerCardV2Props {
  freelancer: FreelancerListItem;
  /** Modo seleção para vaga: mostra o KanbanStageSelector. */
  vagaId?: string;
  /** Estado de seleção do multi-select V2 (independe do KanbanStageSelector). */
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  /** Clique simples abre o painel do candidato; cmd/ctrl-clique segue para a ficha. */
  onOpenDetails?: () => void;
  /** WhatsApp e LinkedIn em poucas linhas, carregados em lote pela página. */
  resumoRapido?: FreelancerResumoRapido | null;
  /** Disponibilidade (v_availability_summary) hidratada em lote pela página. */
  disponibilidade?: DisponibilidadeSummary | null;
  /** Áreas selecionadas no filtro da listagem: aparecem primeiro. */
  filteredAreas?: string[];
  /** Busca livre da lista: termo a marcar e por que o perfil apareceu. */
  searchMatch?: FreelancerSearchMatch;
}

export function FreelancerCardV2({
  freelancer,
  vagaId,
  isSelected = false,
  onToggleSelect,
  onOpenDetails,
  disponibilidade,
  filteredAreas,
  searchMatch,
  resumoRapido,
}: FreelancerCardV2Props) {
  const location = useLocation();
  // No modo server as tags vêm na própria linha da RPC.
  const rating: { tags?: string[] } | null =
    (freelancer as { tags?: string[] }).tags?.length
      ? { tags: (freelancer as { tags?: string[] }).tags }
      : null;

  const fichaUrl = buildFichaUrl(freelancer.id, vagaId, location.search);
  const openPanelOnPlainClick = (event: React.MouseEvent) => {
    if (!onOpenDetails || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onOpenDetails();
  };

  const location_ = formatLocation(freelancer);
  const availability = buildAvailabilityChip(freelancer);
  const attrs = topAttributes(freelancer.atributos_medias, 3);
  const fieldTerm = searchMatch?.fieldTerm ?? "";
  const hiddenReasons = reasonsHiddenFromCard(searchMatch?.reasons ?? [], fieldTerm, location_);

  const isInactive = freelancer.is_active === false;

  return (
    <Card
      data-freelancer-id={freelancer.id}
      className={cn(
        "group relative flex flex-col transition-colors hover:border-primary",
        isInactive && "opacity-60 bg-gray-50 border-gray-300",
        isSelected && "border-primary ring-2 ring-primary/30",
      )}
    >
      <CardContent className="px-4 pt-3 pb-2 flex flex-col gap-2 flex-1">
        {/* Linha 1: avatar + nome + selo + menu */}
        <div className="flex items-start gap-3">
          {onToggleSelect && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(freelancer.id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Selecionar ${freelancer.nome}`}
              className="mt-1"
            />
          )}
          <Link
            to={fichaUrl}
            onClick={openPanelOnPlainClick}
            className="shrink-0 size-10 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-sm hover:bg-primary/20 transition-colors"
            aria-label={`Ver detalhes de ${freelancer.nome}`}
          >
            <FreelancerAvatar
              nome={freelancer.nome}
              fotoUrl={freelancer.linkedin_foto_url}
              openToWork={freelancer.open_to_work}
              openToWorkObservadoEm={freelancer.open_to_work_observado_em}
            />
          </Link>
          <div className="flex-1 min-w-0">
            <Link
              to={fichaUrl}
              onClick={openPanelOnPlainClick}
              className="block text-left w-full min-w-0"
              data-testid="abrir-candidato"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-semibold truncate">
                  <SearchHighlight text={freelancer.nome} term={searchMatch?.nameTerm} />
                </span>
                {isInactive && (
                  <Badge
                    variant="outline"
                    className="shrink-0 border-red-200 bg-red-50 px-1.5 py-0 text-[10px] text-red-600"
                  >
                    inativo
                  </Badge>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <BadgeCheck
                        className={cn(
                          "h-4 w-4 shrink-0",
                          freelancer.quality_seal
                            ? "text-blue-500 fill-blue-500 stroke-amber-200"
                            : "text-gray-300",
                        )}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {freelancer.quality_seal ? "Freelancer verificado" : "Não verificado"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex min-w-0 text-xs text-muted-foreground">
                <FreelancerAreasSummary
                  areas={freelancer.areas}
                  filteredAreas={filteredAreas}
                  searchTerm={fieldTerm}
                  maxVisible={2}
                />
                {location_ && (
                  <span className="min-w-0 truncate whitespace-pre">
                    {freelancer.areas?.length ? " · " : ""}
                    <SearchHighlight text={location_} term={fieldTerm} />
                  </span>
                )}
                {!freelancer.areas?.length && !location_ && "—"}
              </div>
            </Link>
          </div>
          <FreelancerActionsMenu
            freelancerId={freelancer.id}
            freelancerName={freelancer.nome}
            freelancerEmail={freelancer.email}
            isActive={!isInactive}
            copyData={{
              nome: freelancer.nome,
              pretensao_salarial: freelancer.pretensao_salarial,
              areas: freelancer.areas,
              cidade: freelancer.cidade,
              city: freelancer.city,
              state: freelancer.state,
              portfolio_url: freelancer.portfolio_url,
              linkedin_url: freelancer.linkedin_url,
            }}
          />
        </div>

        {/* Linha 2: disponibilidade em linha própria, com a largura toda do card.
            Nunca quebra: o texto do badge trunca se faltar espaço. */}
        <div className="flex flex-nowrap items-center gap-1.5 min-w-0">
          <DisponibilidadeBadge
            summary={disponibilidade}
            level="inline"
            className="min-w-0 text-[11px]"
          />
          <AlocacaoChip summary={disponibilidade} />
          <DisponibilidadeDataHint summary={disponibilidade} />
        </div>

        <FreelancerSearchReasons reasons={hiddenReasons} term={fieldTerm} typedTerm={searchMatch?.nameTerm ?? ""} maxSnippets={2} clampClassName="line-clamp-3" />

        {/* Linha 3: scores destaque à esquerda + sinais à direita */}
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
          <FreelancerScoreBadge freelancer={freelancer} />
          {typeof freelancer.media_avaliacao_perfil === "number" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="bg-purple-50 text-purple-700 border-purple-200 text-xs px-1.5 py-0 h-5"
                  >
                    Perfil {freelancer.media_avaliacao_perfil.toFixed(1)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Média de avaliações de perfil ({freelancer.total_avaliacoes_perfil ?? 0})
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {typeof freelancer.media_avaliacao_projeto === "number" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200 text-xs px-1.5 py-0 h-5"
                  >
                    Projeto {freelancer.media_avaliacao_projeto.toFixed(1)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Média de avaliações de projeto ({freelancer.total_avaliacoes_projeto ?? 0})
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          </div>
          <FreelancerSignalsRow freelancer={freelancer} density="compact" />
        </div>

        {/* Linha 3: top atributos */}
        {attrs.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attrs.map((a) => (
              <Badge
                key={a.slug}
                variant="secondary"
                className="text-[11px] px-1.5 py-0 h-5 font-normal"
                title={`${a.nome}: média ${a.media.toFixed(1)} em ${a.total} avaliação(ões)`}
              >
                <span className="capitalize">{a.nome.toLowerCase()}</span>
                <span className="ml-1 font-semibold">{a.media.toFixed(1)}</span>
              </Badge>
            ))}
          </div>
        )}

        {/* Linha 5: pretensão. Linha 6: renda/situação, sempre em linha própria
            (estrutura fixa deixa os cards com o mesmo desenho). */}
        {freelancer.pretensao_salarial && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <DollarSign className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium text-foreground truncate">
              <SearchHighlight text={freelancer.pretensao_salarial} term={fieldTerm} />
            </span>
          </div>
        )}
        {availability && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Briefcase className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{availability}</span>
          </div>
        )}

        {/* Linha 6: tags manuais (rating.tags) */}
        {rating?.tags && rating.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {rating.tags.slice(0, 4).map((tag) => (
              <Badge key={tag} variant="outline" className="text-[11px] px-1.5 py-0 h-5">
                {tag}
              </Badge>
            ))}
            {rating.tags.length > 4 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-[11px] px-1.5 py-0 h-5 cursor-default"
                    >
                      +{rating.tags.length - 4}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-0.5 max-w-xs">
                      {rating.tags.slice(4).map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        {/* Linha 7: tags automáticas (IA) — visual distinto das manuais */}
        {freelancer.auto_tags && freelancer.auto_tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Sparkles className="h-3 w-3 text-muted-foreground shrink-0" />
                </TooltipTrigger>
                <TooltipContent>Tags automáticas (extraídas por IA)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {freelancer.auto_tags.slice(0, 4).map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-[11px] px-1.5 py-0 h-5 font-normal text-muted-foreground"
              >
                {tag}
              </Badge>
            ))}
            {freelancer.auto_tags.length > 4 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="text-[11px] px-1.5 py-0 h-5 font-normal text-muted-foreground cursor-default"
                    >
                      +{freelancer.auto_tags.length - 4}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-0.5 max-w-xs">
                      {freelancer.auto_tags.slice(4).map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        {/* Última revisão do LinkedIn (quando houver upload aplicado) */}
        {freelancer.linkedin_ultima_revisao && (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-blue-600"
            title={`LinkedIn revisado em ${new Date(freelancer.linkedin_ultima_revisao).toLocaleString("pt-BR")}`}
          >
            <Linkedin className="h-3 w-3" />
            Revisado {formatTimeAgo(freelancer.linkedin_ultima_revisao).text}
          </span>
        )}

        {/* Linha 7: data do cadastro + links externos (fixa no pé do card) */}
        <div className="mt-auto flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {freelancer.last_submitted_at
              ? `Cadastro atualizado ${formatTimeAgo(freelancer.last_submitted_at).text}`
              : "Sem cadastro"}
          </span>
          <div className="flex items-center gap-0.5">
            <WhatsAppIcone resumo={resumoRapido?.whatsapp} telefone={freelancer.telefone} />
            <LinkedInIcone freelancerId={freelancer.id} url={freelancer.linkedin_url} resumo={resumoRapido?.linkedin} iconClassName="h-3.5 w-3.5" />
            <ExternalLinkButton
              kind="portfolio"
              url={freelancer.portfolio_url}
              iconClassName="h-3.5 w-3.5"
            />
            <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Ver ficha completa">
              <Link to={fichaUrl} aria-label="Ver ficha completa">
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Seletor de etapa kanban (modo vaga) */}
        {vagaId && (
          <div className="pt-2 border-t">
            <KanbanStageSelector
              vagaId={vagaId}
              freelancerId={freelancer.id}
              freelancerNome={freelancer.nome}
              freelancerEmail={freelancer.email}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
